import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Eye, Pencil, Plus } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Combobox,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { VehicleType } from "@/configurator/vehicleTypes/vehicleType";
import { formatCentsAsEuros } from "@/configurator/vehicleTypes/vehicleTypeDisplay";
import { toVehicleTypeCreateInput } from "@/configurator/vehicleTypes/vehicleTypeForm";
import { VehicleTypeSheet } from "@/configurator/vehicleTypes/VehicleTypeSheet";
import { useCreateVehicleType } from "@/configurator/vehicleTypes/useCreateVehicleType";
import { useVehicleCategoryOptions } from "@/configurator/vehicleTypes/useVehicleCategoryOptions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorVehicleTypes } from "@/lib/api/generated/configurator/configurator";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_configurator_vehicle_type_create";
const UPDATE_PERMISSION = "admin_configurator_vehicle_type_update";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `vehicle-type-skeleton-${index}`,
);

interface VehicleTypesSearch {
  readonly page: number;
  readonly vehicleCategory?: string;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-types",
)({
  validateSearch: (search: Record<string, unknown>): VehicleTypesSearch => {
    const page = Number(search.page);
    const vehicleCategory =
      typeof search.vehicleCategory === "string" && search.vehicleCategory
        ? search.vehicleCategory
        : undefined;
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      vehicleCategory,
    };
  },
  component: VehicleTypesPage,
});

function VehicleTypesPage(): ReactNode {
  const { page, vehicleCategory } = Route.useSearch();
  const navigate = Route.useNavigate();
  const routerNavigate = useNavigate();

  const { options: categoryOptions, isPending: categoriesPending } =
    useVehicleCategoryOptions();
  const categoryLabels = useMemo(
    () =>
      new Map(categoryOptions.map((option) => [option.value, option.label])),
    [categoryOptions],
  );

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminConfiguratorVehicleTypes(
      { page, vehicleCategory },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const createMutation = useCreateVehicleType();

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  const openEdit = (id: string) =>
    void routerNavigate({
      to: "/configurator/vehicle-types/$vehicleTypeId/edit",
      params: { vehicleTypeId: id },
      search: { tab: "details" },
    });

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Nieuw voertuigtype
          </Button>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Voertuigtypen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Beheer de voertuigtypen die de configurator aanbiedt, inclusief
          opties, servicepartners, dealers en media.
        </p>
      </div>

      <div className="w-full max-w-xs">
        <Combobox
          aria-label="Filter op voertuigcategorie"
          placeholder="Alle categorieën"
          value={vehicleCategory}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                page: 1,
                vehicleCategory: value ?? undefined,
              }),
            })
          }
          options={categoryOptions}
          loading={categoriesPending}
          clearable
          fluid
        />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Merk</Th>
              <Th>Model</Th>
              <Th>Categorie</Th>
              <Th className="w-28 text-right">Prijs</Th>
              <Th className="w-20 text-right">Opties</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-28 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <VehicleTypeRows
              isPending={isPending}
              isError={isError}
              members={members}
              categoryLabels={categoryLabels}
              canUpdate={UPDATE_PERMISSION}
              onEdit={openEdit}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      <VehicleTypeSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        isSubmitting={createMutation.isPending}
        onSubmit={(values) => {
          createMutation.mutate(toVehicleTypeCreateInput(values), {
            onSuccess: (created) => {
              setCreateOpen(false);
              if (created.id) openEdit(created.id);
            },
          });
        }}
      />
    </div>
  );
}

function VehicleTypeRows({
  isPending,
  isError,
  members,
  categoryLabels,
  canUpdate,
  onEdit,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly VehicleType[] | undefined;
  readonly categoryLabels: ReadonlyMap<string, string>;
  readonly canUpdate: string;
  readonly onEdit: (id: string) => void;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-24" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-20" />
        </Td>
        <Td>
          <Skeleton className="ml-auto h-4 w-16" />
        </Td>
        <Td>
          <Skeleton className="ml-auto h-4 w-8" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={7} className="py-10 text-center">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              We konden de voertuigtypen niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={7} className="py-10 text-center text-muted-foreground">
          Nog geen voertuigtypen.
        </Td>
      </Tr>
    );
  }

  return members.map((vehicleType) => {
    const id = vehicleType.id;
    const label = `${vehicleType.brand} ${vehicleType.model}`.trim();
    return (
      <Tr key={vehicleType.id}>
        <Td className="font-medium">
          {id ? (
            <Link
              to="/configurator/vehicle-types/$vehicleTypeId"
              params={{ vehicleTypeId: id }}
              search={{ tab: "details" }}
              className="text-foreground underline-offset-4 hover:underline"
            >
              {vehicleType.brand}
            </Link>
          ) : (
            vehicleType.brand
          )}
        </Td>
        <Td>{vehicleType.model}</Td>
        <Td>
          {vehicleType.vehicleCategory
            ? (categoryLabels.get(vehicleType.vehicleCategory) ?? "—")
            : "—"}
        </Td>
        <Td className="text-right tabular-nums">
          {formatCentsAsEuros(vehicleType.price)}
        </Td>
        <Td className="text-right tabular-nums">
          {vehicleType.options.length}
        </Td>
        <Td>
          {vehicleType.active ? (
            <Badge variant="success" size="sm">
              Actief
            </Badge>
          ) : (
            <Badge variant="neutral" size="sm">
              Inactief
            </Badge>
          )}
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            {id && (
              <Link
                to="/configurator/vehicle-types/$vehicleTypeId"
                params={{ vehicleTypeId: id }}
                search={{ tab: "details" }}
                aria-label={`${label || "voertuigtype"} bekijken`}
                className="inline-flex size-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted/70"
              >
                <Eye className="size-4" aria-hidden="true" />
              </Link>
            )}
            <RequirePermission permission={canUpdate}>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${label || "voertuigtype"} bewerken`}
                onClick={() => id && onEdit(id)}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </Button>
            </RequirePermission>
          </div>
        </Td>
      </Tr>
    );
  });
}
